import { useState } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import {
  useAccountDispatch,
  useAccountSelector,
} from '@wajeez-web/stores/account';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { SalesActions } from '@wajeez-web/stores/account';
import { ButtonHeadless } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AdminSystemPermissions } from '@wajeez-web/core/types';
import { DataTableWrapper } from '@wajeez-web/components/helpers';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { CheckBoxType } from '@wajeez-web/components/mui';
import { ConfirmationModal } from '@wajeez-web/components/ui';
import { Server } from '@wajeez/wajeez.microservices';

import { SaleUserModal } from '../components';
import { AssignPromoCodeToSaleUserModal } from '../components/AssignPromoCodeToSaleUserModal';
import { AssignPublisherToUtilityUserModal } from '../components/AssignPublisherToSaleUserModal';

export const SaleUsersPage = () => {
  const accountDispatch = useAccountDispatch();

  const [isSaleUserModalOpen, setIsSaleUserModalOpen] = useState(false);
  const [
    isAssingBranchToSaleUserModalOpen,
    setIsAssignBranchToSaleUserModalOpen,
  ] = useState(false);
  const [selectedSaleUser, setSelectedSaleUser] = useState<
    { id: string; state: Server.UtilityUserState } | undefined
  >();
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: '' });
  const [
    isAssingPromoCodeAliasToSaleUserModalOpen,
    setIsAssignPromoCodeAliasToSaleUserModalOpen,
  ] = useState(false);
  const [
    isAssingPublisherToUtilityUserModalOpen,
    setIsAssignPublisherToUtilityUserModalOpen,
  ] = useState(false);

  const saleUsersDatatable = useAccountSelector(
    ({ sales }) => sales.utilityUsersDatatable
  );
  const updateSaleUserActivationStatus = useAccountSelector(
    ({ sales }) => sales.updateUtilityUserActivation.status
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData,
    isTableReferencesLoading,
  } = HandleTableData('saleUsers', SalesActions.getUtilityUsersDatatableAsync);

  return (
    <div className="h-full">
      <PageLayoutsContainer
        title={`Sale Users`}
        withoutRightPadding={false}
        breadCrumbsLinks={[{ title: 'Sale Users' }]}
      >
        <div className="w-full">
          <div className="w-full flex justify-end items-center">
            <div className="w-[110px] mb-6">
              <ButtonHeadless
                paddingYStyle="py-[6px]"
                borderRadiusStyle="rounded-[6px]"
                action={() => {
                  setIsSaleUserModalOpen(true);
                }}
              >
                <div className="text-[15px] text-white">
                  <ReturnTranslatedText
                    text={`buttons.${'Create User'}`}
                    origintext="Create User"
                  />
                </div>
              </ButtonHeadless>
            </div>
          </div>
          <DataTableWrapper
            data={saleUsersDatatable.data as contentEntityDataType}
            onHeaderManage={(header) => {
              const flags = header
                ?.filter((h) => h.selected)
                .reduce(
                  (previousValue: number, currentValue: CheckBoxType) =>
                    previousValue + Number(currentValue.value),
                  0
                );

              tablePreferances &&
                flags &&
                setTablePreferences({
                  ...tablePreferances,
                  flags: flags + 1,
                });
            }}
            onPaginationChange={(pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  pageSize: pagination.pageSize,
                });
            }}
            sort={tablePreferances?.sortingOperations?.[0]}
            onSort={(sort) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  sortingOperations: [sort],
                });
            }}
            onFilter={(filters, pagination) => {
              tablePreferances &&
                setTablePreferences({
                  ...tablePreferances,
                  pageNumber: pagination.pageNumber,
                  filteringOperations: filters.map((f) => {
                    return {
                      property: Number(f.key),
                      cretiria: f.value,
                      comparisonType: f.operator,
                    };
                  }),
                });
            }}
            isLoading={saleUsersDatatable.status === 'loading'}
            rowActions={(row) => {
              const activated = row[8] === 'Activated';

              return [
                {
                  title: 'Assign Publisher',
                  endpoint: '',
                  permission: AdminSystemPermissions.saleUsers.assignPublisher,
                  type: 'request',
                  action: () => {
                    setSelectedSaleUser({
                      id: row[1] as string,
                      state: activated
                        ? Server.UtilityUserState.Activated
                        : Server.UtilityUserState.Disabled,
                    });
                    setIsAssignPublisherToUtilityUserModalOpen(true);
                  },
                },
                {
                  title: 'Assign Promo Code Alias',
                  endpoint: '',
                  permission:
                    AdminSystemPermissions.saleUsers.assignPromoCodeAlias,
                  type: 'request',
                  action: () => {
                    setSelectedSaleUser({
                      id: row[1] as string,
                      state: activated
                        ? Server.UtilityUserState.Activated
                        : Server.UtilityUserState.Disabled,
                    });
                    setIsAssignPromoCodeAliasToSaleUserModalOpen(true);
                  },
                },
                {
                  title: activated ? 'Deactivate' : 'Activate',
                  endpoint: '',
                  permission: AdminSystemPermissions.saleUsers.updateActivation,
                  type: 'request',
                  action: () => {
                    setSelectedSaleUser({
                      id: row[1] as string,
                      state: activated
                        ? Server.UtilityUserState.Activated
                        : Server.UtilityUserState.Disabled,
                    });
                    setConfirmationModal({
                      isOpen: true,
                      message: `Are you sure that you want to ${activated ? 'deactivate' : 'activate'
                        } this sale user?`,
                    });
                  },
                },
              ];
            }}
            preferences={tablePreferances || undefined}
            setPreferences={setTablePreferences}
          />
        </div>
      </PageLayoutsContainer>
      {isSaleUserModalOpen && (
        <SaleUserModal
          isOpen={isSaleUserModalOpen}
          onClose={() => {
            setIsSaleUserModalOpen(false);
          }}
          onSubmit={() => {
            setIsSaleUserModalOpen(false);

            getData();
          }}
        />
      )}
      {isAssingPublisherToUtilityUserModalOpen && (
        <AssignPublisherToUtilityUserModal
          isOpen={isAssingPublisherToUtilityUserModalOpen}
          onClose={() => {
            setIsAssignPublisherToUtilityUserModalOpen(false);

            setSelectedSaleUser(undefined);
          }}
          onSubmit={() => {
            setIsAssignPublisherToUtilityUserModalOpen(false);

            setSelectedSaleUser(undefined);

            getData();
          }}
          saleUserId={Number(selectedSaleUser?.id)}
        />
      )}
      {isAssingPromoCodeAliasToSaleUserModalOpen && (
        <AssignPromoCodeToSaleUserModal
          isOpen={isAssingPromoCodeAliasToSaleUserModalOpen}
          onClose={() => {
            setIsAssignPromoCodeAliasToSaleUserModalOpen(false);

            setSelectedSaleUser(undefined);
          }}
          onSubmit={() => {
            setIsAssignPromoCodeAliasToSaleUserModalOpen(false);

            setSelectedSaleUser(undefined);

            getData();
          }}
          saleUserId={Number(selectedSaleUser?.id)}
        />
      )}
      {confirmationModal.isOpen && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          message={confirmationModal.message}
          onClose={() => {
            setConfirmationModal({ isOpen: false, message: '' });
          }}
          onSubmit={async () => {
            const res = await accountDispatch(
              SalesActions.updateUtilityUserActivationAsync({
                id: Number(selectedSaleUser?.id),
                utilityUserState:
                  selectedSaleUser?.state === Server.UtilityUserState.Activated
                    ? Server.UtilityUserState.Disabled
                    : Server.UtilityUserState.Activated,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setConfirmationModal({ isOpen: false, message: '' });

              tablePreferances && setTablePreferences({ ...tablePreferances });
            }
          }}
          isLoading={updateSaleUserActivationStatus === 'loading'}
        />
      )}
    </div>
  );
};
